import { defineStore } from 'pinia'
import { getIndexerDerivativesApi } from '@shared/service'
import type { Orderbook, PositionV2 } from '@injectivelabs/sdk-ts'
import type { ActivityFetchOptions } from '@/types'

type OrderBookMap = Record<string, Orderbook>

type PositionStoreState = {
  positions: PositionV2[]
  orderbooks: OrderBookMap
}

const initialStateFactory = (): PositionStoreState => ({
  orderbooks: {} as OrderBookMap,
  positions: []
})

export const usePositionStore = defineStore('position', {
  state: (): PositionStoreState => initialStateFactory(),
  getters: {
    positionsBySubaccountId: (state) => (subaccountId: string) => {
      const derivativeStore = useDerivativeStore()

      return state.positions.filter(
        (position) =>
          position.subaccountId === subaccountId &&
          derivativeStore.activeMarketIds.includes(position.marketId)
      )
    },

    subaccountPositions: (state) => {
      const accountStore = useAccountStore()
      const derivativeStore = useDerivativeStore()

      return state.positions.filter(
        ({ marketId, subaccountId }) =>
          subaccountId === accountStore.subaccountId &&
          derivativeStore.activeMarketIds.includes(marketId)
      )
    }
  },
  actions: {
    closePosition: lazyPiniaAction(() => import('./message'), 'closePosition'),
    closeAllPosition: lazyPiniaAction(
      () => import('./message'),
      'closeAllPosition'
    ),
    addMarginToPosition: lazyPiniaAction(
      () => import('./message'),
      'addMarginToPosition'
    ),
    addMarginToSubaccountPosition: lazyPiniaAction(
      () => import('./message'),
      'addMarginToSubaccountPosition'
    ),

    streamAccountPositions: lazyPiniaAction(
      () => import('./stream'),
      'streamAccountPositions'
    ),
    cancelAccountPositionsStream: lazyPiniaAction(
      () => import('./stream'),
      'cancelAccountPositionsStream'
    ),

    async fetchPositions() {
      const indexerDerivativesApi = await getIndexerDerivativesApi()

      const positionStore = usePositionStore()
      const derivativeStore = useDerivativeStore()
      const sharedWalletStore = useSharedWalletStore()

      if (
        !sharedWalletStore.isUserConnected ||
        !sharedWalletStore.authZOrInjectiveAddress
      ) {
        return
      }

      const { positions } = await indexerDerivativesApi.fetchPositionsV2({
        address: sharedWalletStore.authZOrInjectiveAddress
      })

      derivativeStore.updateMarkPriceMapFromPosition(positions)

      positionStore.positions = positions
    },

    async fetchSubaccountPositions(
      activityFetchOptions?: ActivityFetchOptions
    ) {
      const indexerDerivativesApi = await getIndexerDerivativesApi()

      const accountStore = useAccountStore()
      const positionStore = usePositionStore()
      const derivativeStore = useDerivativeStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected || !accountStore.subaccountId) {
        return
      }

      const filters = activityFetchOptions?.filters

      const { positions } = await indexerDerivativesApi.fetchPositionsV2({
        subaccountId: accountStore.subaccountId,
        marketIds: filters?.marketIds || derivativeStore.activeMarketIds,
        direction: filters?.direction
      })

      derivativeStore.updateMarkPriceMapFromPosition(positions)

      const filteredExistingPositions = positionStore.positions.filter(
        (position) =>
          !positions.some(
            (p) =>
              p.marketId === position.marketId &&
              p.subaccountId === position.subaccountId
          )
      )

      positionStore.positions = [...filteredExistingPositions, ...positions]
    },

    // Todo: @ivan verify if we still needs this
    // Fetching multiple market orderbooks for unrealized PnL calculation within a market page
    async fetchOpenPositionsMarketsOrderbook() {
      const indexerDerivativesApi = await getIndexerDerivativesApi()

      const accountStore = useAccountStore()
      const positionStore = usePositionStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected || !accountStore.subaccountId) {
        return
      }

      if (positionStore.subaccountPositions.length === 0) {
        return
      }

      const marketsOrderbook = await indexerDerivativesApi.fetchOrderbooksV2(
        positionStore.subaccountPositions.map((position) => position.marketId)
      )
      const marketsOrderbookMap = marketsOrderbook.reduce(
        (marketOrderbooks, { orderbook }, index) => {
          const position = positionStore.subaccountPositions[index]
          if (!position) {
            return marketOrderbooks
          }

          return {
            ...marketOrderbooks,
            [position.marketId]: orderbook
          }
        },
        {} as OrderBookMap
      )

      positionStore.orderbooks = marketsOrderbookMap
    }
  }
})
